import { isPossibleNumber, isValidPhoneNumber } from 'libphonenumber-js';

import {
  ExtendedFieldMappingEditorValue,
  FieldMappingEditorValue,
} from '@components/text-editors/addons/field-mapping';
import { htmlToPlaintext } from '@utils/converters';
import { isNotFalse } from '@utils/types';
import {
  accountNumber,
  bankingNumber,
  isBusinessEmail,
  isColorCode,
  isDecimal,
  isOnlyDigits,
  isOnlyPosDigits,
  isValidEmailSenderName,
  isValidHttpUrl,
  isValidJson,
  isValidSmsSenderName,
  isValidSsn,
  isValidUrl,
  ledger,
  validateEmail,
  validatePasswordLength,
} from '@utils/validators';

import { FormRegisteredFieldData, FormValidatorType, FormValidatorTypeString } from './types';

export const validateFormValue = (
  value: unknown,
  validators?: Array<FormValidatorType>,
  valueType?: FormRegisteredFieldData['valueType']
): Array<FormValidatorTypeString | string> => {
  if (!validators) return [];

  const errors = validators.map((validator) => {
    if (!validator) return false;

    if (typeof validator === 'function') {
      const validatorValue = validator(value);
      return validatorValue === true ? false : validatorValue;
    }

    const isValid = isFieldValid(validator, value, valueType);
    return !isValid && validator;
  });

  return errors.filter(isNotFalse);
};

const isFieldValid = (
  validator: FormValidatorType,
  value: unknown,
  valueType?: FormRegisteredFieldData['valueType']
) => {
  if (value === undefined || value === null) {
    return validator !== 'required';
  }

  if (valueType === 'field-mapping-list') {
    if (validator === 'required') {
      return (value as ExtendedFieldMappingEditorValue[]).length > 0;
    }
    if (validator === 'email') {
      return (value as ExtendedFieldMappingEditorValue[]).every(
        (email) => (email as FieldMappingEditorValue).variables || validateEmail(email.value)
      );
    }
  }

  if (valueType === 'text-editor' && validator === 'required') {
    const html = typeof value === 'string' ? value : value['value' as keyof typeof value];
    const plaintext = html ? htmlToPlaintext(html) : '';
    return plaintext.length > 0;
  }

  if (Array.isArray(value) && validator === 'required') {
    return value.length > 0;
  }

  if (typeof value !== 'string') {
    return validator === 'required';
  }

  switch (validator) {
    case 'email':
      if (!value) return true;
      return validateEmail(value);
    case 'required':
      return value !== '';
    case 'account-number':
      return accountNumber(value);
    case 'bank-number':
      return bankingNumber(value);
    case 'no-default-value':
      return value !== 'defaultValue';
    case 'ledger':
      return ledger(value);
    case 'number':
      if (!value) return true;
      return isOnlyDigits(value);
    case 'decimal':
      if (!value) return true;
      return isDecimal(value);
    case 'ssn':
      return isValidSsn(value);
    case 'phoneNumber':
    case 'phone':
      if (!value) return true;
      return isValidPhoneNumber(value, 'IS') || isPossibleNumber(value, 'IS');
    case 'color-code':
      return isColorCode(value);
    case 'password':
      return validatePasswordLength(value);
    case 'valid-http-url':
      if (!value) return true;
      return isValidHttpUrl(value);
    case 'positive-number':
      return isOnlyPosDigits(value);
    case 'url':
      return isValidUrl(value);
    case 'json':
      return isValidJson(value);
    case 'sms-sender-name':
      return isValidSmsSenderName(value);
    case 'email-sender-name':
      return isValidEmailSenderName(value);
    case 'business-email':
      return isBusinessEmail(value);
    default:
      return true;
  }
};
